import { ChevronRight } from "lucide-react";
import { Fragment, useId, useState, type ReactNode } from "react";

import { cn } from "@/lib/cn";
import { DataRow } from "@/ui/DataRow";
import { Tooltip } from "@/ui/Tooltip";

/**
 * The compact catalog row, its metadata line and its Details disclosure (#908).
 *
 * ## THE ROW HEIGHT IS THE TICKET
 *
 * The observed gap is "tall generic cards": at the repository's 1280-wide
 * baseline the shared catalog rendered each asset as a stacked block — name,
 * then the type on a second line, then a wrapped row of badges and provenance,
 * then the lifecycle action on a fourth — about 147px per asset, so four assets
 * filled the viewport. The reference puts the same facts on ONE line of about
 * 62px: name at the start, a single dot-separated metadata string, the row's
 * action at the end.
 *
 * The height came from the columns, not from the padding. Six independent
 * tracks (Asset · State · Version · Author · Updated · Adoption) cannot fit the
 * ~712px this card actually gets beside the application sidebar and the library
 * rail, so they wrapped, and the stacked fallback is what a clinician saw at
 * every ordinary width. `CatalogMeta` replaces them with one flowing line, which
 * is why the row fits where the grid could not — and why the facts are still all
 * there rather than dropped to make room.
 *
 * ## STILL `DataRow` UNDERNEATH
 *
 * `DataRow` owns the one structural guarantee on this surface: navigation is a
 * real `<a>` stretched across the row, actions are painted above it, and there
 * is no row-level click handler for an action's click to bubble into — the fix
 * for the reference's own defect where clicking a row action also navigates.
 * Compactness is a matter of padding and of what goes in `columns`, so this
 * composes that component rather than reimplementing a row that would have to
 * re-earn the guarantee.
 */

/**
 * One flowing metadata line, dot-separated.
 *
 * EACH FACT IS ITS OWN ELEMENT and the separators are `aria-hidden`. A single
 * interpolated string would read to a screen reader as one run-on sentence, and
 * would leave "the author" and "the updated date" with no distinct node — which
 * is what the catalog's provenance assertions address. Callers pass entries they
 * have already decided to show; an absent fact is an absent entry, never an
 * empty cell.
 */
export function CatalogMeta({
  items,
  className,
}: {
  items: readonly ReactNode[];
  className?: string;
}) {
  const shown = items.filter((item) => item !== null && item !== undefined);

  return (
    <span
      className={cn(
        "flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground",
        className,
      )}
    >
      {shown.map((item, index) =>
        index === 0 ? (
          <Fragment key={index}>{item}</Fragment>
        ) : (
          // The separator travels WITH the fact it precedes. As its own flex
          // item it could be left stranded at the end of a wrapped line — at
          // 375px the adoption count drops to a second line and the row ended
          // in a dot with nothing after it. Grouped, a wrap puts the dot in
          // front of the fact it introduces, which reads as a continuation.
          <span key={index} className="flex items-center gap-x-2">
            <span aria-hidden="true" className="text-border">
              ·
            </span>
            {item}
          </span>
        ),
      )}
    </span>
  );
}

/**
 * The Details disclosure: technical evidence a clinician can ask for, and does
 * not have to read past.
 *
 * The bounded scope is explicit that internal part/path/engine explanations
 * leave normal navigation and catalog copy while the useful evidence stays
 * available — so this is where a row's engine-owned document counts, exact
 * placement and similar facts belong. Collapsed by default and mounted only
 * when open, so a collapsed row's contents are absent from the accessibility
 * tree rather than merely invisible.
 *
 * THE ACCESSIBLE NAME CARRIES THE SUBJECT. A catalog holds many rows, and a
 * page of buttons all named "Details" is the repeated-ambiguous-control defect
 * the parity audit already recorded once. It is written once, in a `sr-only`
 * span, rather than assembled from a painted word plus a hidden suffix:
 * accessible-name computation trims each text node and concatenates inline
 * content with no separator, so `Details` + ` for Anxiety` computes as
 * "Detailsfor Anxiety" (`dom-accessibility-api`, and the spec it follows).
 *
 * ## ICON-ONLY ON A ROW, AND WHY THAT IS THE FAITHFUL FORM
 *
 * The reference's catalog rows end in a chevron and carry no word: its variant
 * rows have no disclosure at all, and its family header's is `Collapse`,
 * icon-only. The word `Details` belongs to the reference's ASSET page, which is
 * a different surface with room for it.
 *
 * It is also what makes the row fit. Measured on the seeded `Agoraphobia`
 * variant at the 1280px baseline, a member row has 670px and its contents want
 * 713: metadata 316, `Create editable copy` 163, this control 55, three gaps 36
 * and 167 for the name. A labelled control spends 55px of that on a word every
 * row repeats, and the 43px overflow is what pushed both actions onto a second
 * line. Icon-only costs 20px and the name survives — the affordance is carried
 * by the tooltip and the accessible name, which is the house pattern for every
 * other icon control on this surface.
 */
export function CatalogDetails({
  subject,
  children,
}: {
  /** What the details are ABOUT — an asset or family name. */
  subject: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const bodyId = useId();

  const label = `Details for ${subject}`;

  return (
    // TWO FLEX ITEMS OF THE CALLER'S ROW, not a wrapper of its own. The control
    // sits at the END of the row it belongs to — where the reference puts its
    // family chevron — and the panel takes a full basis so it wraps onto its
    // own line beneath. Wrapping the pair in a block would have cost the row a
    // permanent second line for a control one chevron wide, which is most of
    // what made these rows tall in the first place.
    <>
      <Tooltip content={label}>
        <button
          type="button"
          aria-expanded={open}
          aria-controls={bodyId}
          // `relative z-10`, like a row action: a catalog row's whole surface
          // is a stretched link, and a control painted under it never receives
          // the click.
          className="relative z-10 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-sm text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
          onClick={() => setOpen((value) => !value)}
        >
          <ChevronRight
            aria-hidden="true"
            className={cn("h-4 w-4 transition-transform", open && "rotate-90")}
          />
          <span className="sr-only">{label}</span>
        </button>
      </Tooltip>

      {open ? (
        <div id={bodyId} className="relative z-10 mt-1 basis-full ps-4">
          {children}
        </div>
      ) : null}
    </>
  );
}

/**
 * One asset on a catalog: name, compact metadata, an action, optional details.
 *
 * `meta` is rendered by `CatalogMeta` above, in `DataRow`'s subtitle slot;
 * `titleAdornment` carries the state badges, which sit beside the name rather
 * than in the metadata line because they are the one fact a clinician scans for
 * down the column of names.
 */
export function CatalogRow({
  to,
  name,
  titleAdornment,
  meta,
  actions,
  details,
}: {
  to: string;
  name: ReactNode;
  titleAdornment?: ReactNode;
  meta: readonly ReactNode[];
  actions?: ReactNode;
  /** Technical evidence, shown under the row behind a Details disclosure. */
  details?: ReactNode;
}) {
  return (
    <DataRow
      to={to}
      title={name}
      {...(titleAdornment ? { titleAdornment } : {})}
      // `py-2.5` rather than `DataRow`'s `py-4`: this is the compact catalog
      // row, and the reference's is 62px tall including its own border.
      className="gap-y-1 py-2.5"
      /*
        THE METADATA IS THE ROW'S SUBTITLE, not a trailing cell.

        Every fact on it names itself — `Created by …`, `Updated …`, `Used by …`
        — because one flowing line has no column header to say which principal
        and which date these are. That costs about 110px of label text, and at
        the 1280px baseline the card is ~712px: as a trailing cell the line
        wanted 512px for the metadata, 36 for the lifecycle action and 32 for
        gaps, leaving 92px for the name. Squeezed into 92px the name wrapped to
        three lines and the row reached 89px — the tall card, back by a
        different route.

        `DataRow`'s own `subtitle` slot is the shape that fits: the name and its
        state badges on the first line with the action beside them, the metadata
        under the name on the second. That is 62px, and it is what the
        reference's own BG-set rows look like — name over a second line — rather
        than what its three-column Summary rows do. Nothing truncates and
        nothing is dropped. Putting the metadata in `columns` with a full basis
        does NOT work: it is a flex item before `actions`, so wrapping it takes
        the action down to a third line with it.
      */
      subtitle={<CatalogMeta items={meta} />}
      columns={details}
      {...(actions ? { actions } : {})}
    />
  );
}
